import axios from "axios";
import { msalInstance } from "./index";
import { API_URL } from "./env";
import { acquireAccessToken, forceLogout } from "./utils/authSession";

const api = axios.create({
    baseURL: API_URL,
});

// Debug logging
console.log('[AXIOS DEBUG] Configured with baseURL:', API_URL);

api.interceptors.request.use(async (config) => {
    try {
        const accessToken = await acquireAccessToken(msalInstance);
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
    } catch (error) {
        console.error('Failed to acquire access token for API request:', error);
        throw error;
    }
    return config;
});

// Response interceptor to handle session invalidation globally
api.interceptors.response.use(
    (response) => {
        // Check if response data indicates deleted user/organization
        const url = response.config?.url || '';

        // For /users/me endpoint, check if user data is valid
        if (url.includes('/users/me')) {
            if (!response.data || !response.data.id) {
                return Promise.reject(new Error('User not found'));
            }
        }

        // For organization endpoints, check if organization data is valid
        if (url.includes('/organizations') && !url.includes('/frameworks') && !url.includes('/documents') && !url.includes('/users')) {
            // If it's an array (list response), we don't expect a top-level id
            if (Array.isArray(response.data)) {
                return response;
            }

            if (!response.data || (!response.data.id && !response.data.organization_id && !response.data.client_org_id)) {
                return Promise.reject(new Error('Organization not found'));
            }
        }

        return response;
    },
    async (error) => {
        const status = error.response?.status;
        // Handle session invalidation errors
        // ONLY redirect on 401 (Unauthorized). 
        // 403 (Forbidden) and 404 (Not Found) should be handled by the calling component.
        if (status === 401) {
            await forceLogout(msalInstance);
        } else if (status === 403 && !error.config?.url?.includes('/session/refresh')) {
            try {
                await api.post('/session/refresh');
            } catch (refreshError) {
                if (refreshError.response?.status === 401) {
                    await forceLogout(msalInstance);
                }
            }
        }

        return Promise.reject(error);
    }
);

export default api;
